<script setup lang="ts">
import type { FormInput } from "~/components/DynamicForm.vue";
import { formatHttpError } from "~/utils/apiError";
import { emailFromQuery } from "~/utils/emailFromQuery";
import { firstQueryValue } from "~/utils/firstQueryValue";

const authStore = useAuthStore();
const route = useRoute();
const { staticUrl } = useImageUrl();

definePageMeta({
  layout: "static",
});

// This page is indexable (see nuxt.config routeRules) and is what should answer
// "anddine sign up", so the copy has to do more than name the form.
useSeoMeta({
  title: "Sign Up | &Dine Office Catering London",
  description:
    "Create your free &Dine account to order office catering in London from independent makers. Team lunches, meetings and events, delivered.",
});

useHead({
  link: [{ rel: "canonical", href: "https://anddine.co.uk/sign-up" }],
});

const autoActivate = ref(false);
const showPasswordError = ref(false);
const showAddress = ref(false);

// See `firstQueryValue`: a repeated param is an array, not a string, and a second
// `decodeURI` of an already-decoded value throws URIError on `?firstName=%`. Both
// crashed this page in `setup`, i.e. a 500 — and this PR makes it the page Google
// ranks and the target of the /organisation-sign-up redirect, so it is the front door.
//
// `emailFromQuery` on the email only — it restores the `+` vue-router decoded to a
// space, which `getOrgCustomer` below needs to match the invite. Names stay on
// `firstQueryValue`, or "Mary Jane" would become "Mary+Jane".
const urlEmail = emailFromQuery(route.query.email);
const urlFirstName = firstQueryValue(route.query.firstName);
const urlLastName = firstQueryValue(route.query.lastName);


const personalInputs = ref<FormInput[]>([
  {
    title: "Company name",
    type: "text",
    required: true,
    showTitle: false,
    id: "business_name",
    name: "company-name",
    error: "",
    show: true,
    value: "",
    width: "96%",
  },
  {
    title: "First name",
    type: "text",
    required: true,
    showTitle: false,
    id: "first_name",
    name: "given-name",
    error: "",
    show: true,
    value: urlFirstName,
    width: "96%",
  },
  {
    title: "Last name",
    type: "text",
    required: true,
    showTitle: false,
    id: "last_name",
    name: "family-name",
    error: "",
    show: true,
    value: urlLastName,
    width: "96%",
  },
  {
    title: "Email address",
    type: "email",
    required: true,
    showTitle: false,
    id: "email",
    name: "email",
    error: "",
    show: true,
    value: urlEmail,
    width: "96%",
  },
  {
    title: "Phone number",
    type: "phone",
    required: true,
    showTitle: false,
    name: "tel",
    width: "96%",
    id: "phone",
    error: "",
    show: true,
    value: "",
  },
  {
    title: "Password",
    subtitle: "(must be at least 8 characters)",
    type: "password",
    required: true,
    showTitle: false,
    name: "new-password",
    id: "password",
    error: "",
    show: true,
    value: "",
    width: "96%",
  },
  {
    title: "Confirm password",
    subtitle: "(must be at least 8 characters)",
    type: "password",
    required: true,
    showTitle: false,
    name: "new-password",
    id: "new_password",
    error: "",
    show: true,
    value: "",
    width: "96%",
  },
]);

const addressInputs = ref<FormInput[]>([
  {
    title: "Building name, street name and number",
    type: "text",
    id: "line_1",
    required: true,
    showTitle: false,
    name: "address-line1",
    error: "",
    show: true,
    value: "",
    width: "100%",
  },
  {
    title: "Neighbourhood, town or area (optional)",
    type: "text",
    id: "line_2",
    required: false,
    showTitle: false,
    name: "address-line2",
    error: "",
    show: true,
    value: "",
    width: "100%",
  },
  {
    title: "City",
    type: "text",
    id: "city",
    required: false,
    showTitle: false,
    name: "address-level2",
    error: "",
    show: true,
    value: "",
    width: "100%",
  },
  {
    title: "Postcode",
    type: "postcode",
    uppercase: true,
    id: "zip",
    required: false,
    showTitle: false,
    name: "postal-code",
    error: "",
    show: true,
    value: "",
    width: "100%",
  },
]);

const signUpSuccess = ref(false);
const signUpError = ref("");
const customerLoading = ref(false);

const personalErrors = computed(() => {
  const passwords = personalInputs.value.filter((input) => input.type === "password");
  const passwordMismatch = passwords.length === 2 && passwords[0].value !== passwords[1].value;
  return (
    personalInputs.value.some((input) => input.error !== "") ||
    personalInputs.value
      .filter((input) => input.required)
      .some((input) => input.value === "" || input.value === undefined) ||
    passwordMismatch
  );
});

const addressErrors = computed(() => {
  return (
    addressInputs.value.some((input) => input.error !== "") ||
    addressInputs.value
      .filter((input) => input.required)
      .some((input) => input.value === "" || input.value === undefined)
  );
});

const firstName = computed(
  () => personalInputs.value.find((i) => i.id === "first_name")?.value || "",
);

onMounted(async () => {
  // Load HubSpot form for CRM lead capture
  loadHubspotForm("signUpForm", "44eadcd5-bacf-447c-afa5-ae40b59430cc");

  if (!urlEmail) return

	try {
    const customer = await authStore.getOrgCustomer(urlEmail);
    if (!customer) return;

    autoActivate.value = customer.auto_activate ?? false;
    personalInputs.value = personalInputs.value.map((input) => {
      if (input.id === "business_name") input.show = false;
      const value = (customer as Record<string, unknown>)[input.id];
      if (typeof value === "string" && value && !input.value) input.value = value;
      return input;
    });

    const deliveryAddress = customer.organisation?.delivery_address;
    if (deliveryAddress) {
      addressInputs.value.find((i) => i.id === "line_1")!.value = deliveryAddress.line_1 || "";
      addressInputs.value.find((i) => i.id === "line_2")!.value = deliveryAddress.line_2 || "";
      addressInputs.value.find((i) => i.id === "city")!.value = deliveryAddress.city || "";
      addressInputs.value.find((i) => i.id === "zip")!.value = deliveryAddress.zip || "";
    }
  } catch {
    // Non-org sign-ups land here when no pending record exists — ignore.
  }
});

function updatePersonalForm(form: FormInput[]) {
  personalInputs.value = form;
  const passwords = form.filter((field) => field.type === "password");
  showPasswordError.value = passwords.length === 2 && passwords[0].value !== passwords[1].value;
}

function updateAddressForm(form: FormInput[]) {
  addressInputs.value = form;
}

function returnDeliveryAddress(address: { formatted: Record<string, string> }) {
  addressInputs.value.map((input) => (input.value = address.formatted[input.id] || ""));
}

// HubSpot form integration for CRM lead capture
function loadHubspotForm(containerId: string, formId: string) {
  if (typeof window === "undefined") return;
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = "https://js.hsforms.net/forms/v2.js";
  script.async = true;
  script.defer = true;
  script.onload = () => {
    if ((window as any).hbspt) {
      (window as any).hbspt.forms.create({
        region: "eu1",
        portalId: "139704907",
        formId,
        target: `#${containerId}`,
        cssClass: "hs-no-padding",
      });
    }
  };
  document.body.appendChild(script);
}

function submitHubspotForm() {
  if (typeof window === "undefined") return;
  const form = document.querySelector(
    "#hsForm_44eadcd5-bacf-447c-afa5-ae40b59430cc",
  ) as HTMLFormElement;
  if (!form) return;
  const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement | null;
  const firstNameInput = form.querySelector('input[name="firstname"]') as HTMLInputElement | null;
  const lastNameInput = form.querySelector('input[name="lastname"]') as HTMLInputElement | null;
  const companyInput = form.querySelector('input[name="company"]') as HTMLInputElement | null;
  const email = personalInputs.value.find((i) => i.id === "email")?.value;
  const firstName = personalInputs.value.find((i) => i.id === "first_name")?.value;
  const lastName = personalInputs.value.find((i) => i.id === "last_name")?.value;
  const company = personalInputs.value.find((i) => i.id === "business_name")?.value;
  if (emailInput && email) emailInput.value = email;
  if (firstNameInput && firstName) firstNameInput.value = firstName;
  if (lastNameInput && lastName) lastNameInput.value = lastName;
  if (companyInput && company) companyInput.value = company;
  form.submit();
}

async function goToAddress() {
  submitHubspotForm();
  if (autoActivate.value) {
    await signUp();
    return;
  }
  showAddress.value = true;
}

async function signUp() {
  customerLoading.value = true;
  signUpError.value = "";
  const flatObject: Record<string, unknown> = { marketing: true, address: {} };
  personalInputs.value.forEach((item) => {
    flatObject[item.id] = item.value;
  });
  addressInputs.value.forEach((item) => {
    (flatObject.address as Record<string, string>)[item.id] = item.value;
  });
  try {
    await authStore.register(flatObject);
    signUpSuccess.value = true;
  } catch (e: unknown) {
    signUpError.value = formatHttpError(
      e,
      "We couldn't create your account. Please try again.",
    );
  } finally {
    customerLoading.value = false;
  }
}
</script>

<template>
  <section class="sign-up">
    <!-- Hidden HubSpot form for CRM lead capture -->
    <div
      id="signUpForm"
      style="visibility: hidden; height: 0; width: 0; overflow: hidden; position: absolute"
    ></div>
    <div class="sign-up__left">
      <img
        :src="staticUrl('/Images/b-roll/sign-up.jpg')"
        alt="sign-up"
        loading="eager"
      />
    </div>
    <div class="sign-up__right">
      <h3 class="pink-text">Sign up to &amp;Dine<span v-if="firstName">, {{ firstName }}</span></h3>
      <div v-if="!signUpSuccess">
        <div v-if="!showAddress">
          <DynamicForm
            :inputs="personalInputs"
            @return-form="updatePersonalForm"
            @enter-pressed="!personalErrors && goToAddress()"
          />
          <BannerError :message="showPasswordError ? 'Passwords do not match' : ''" />
          <DynamicButton
            color="pink"
            width="100%"
            :icon="customerLoading ? 'spinner' : ''"
            :text="!customerLoading ? (autoActivate ? 'Create Account' : 'Next') : ''"
            :spinning="customerLoading"
            :disabled="personalErrors"
            @click-event="goToAddress"
          />
        </div>
        <div v-else>
          <LocationBox
            :inputs="addressInputs"
            width="100%"
            @address-changed="returnDeliveryAddress"
          />
          <DynamicForm
            :inputs="addressInputs"
            @return-form="updateAddressForm"
            @enter-pressed="!addressErrors && signUp()"
          />
          <BannerError :message="signUpError" />
          <DynamicButton
            color="white"
            width="100%"
            text="Back"
            @click-event="showAddress = false"
          />
          <DynamicButton
            color="pink"
            width="100%"
            :icon="customerLoading ? 'spinner' : ''"
            :text="!customerLoading ? 'Create Account' : ''"
            :spinning="customerLoading"
            :disabled="addressErrors"
            @click-event="signUp"
          />
        </div>
      </div>
      <p v-else-if="!autoActivate">
        Your account has been successfully created, please check your email for a link to verify
        your account.
      </p>
      <p v-else>Your account has been successfully created. Please login.</p>
    </div>
  </section>
</template>

<style lang="scss">
.sign-up {
  position: relative;
  width: 100%;
  display: flex;
  height: 100vh;
  overflow: hidden;
  &__left {
    width: 50%;
    height: 100%;
    overflow: hidden;
    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center top;
    }
    @media (max-width: $tablet) {
      display: none;
    }
  }
  &__right {
    padding: 6rem 10% 3rem;
    width: 30%;
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    overflow-y: auto;
    button {
      margin: 1rem 0;
    }
    @media (max-width: $tablet) {
      width: 100%;
      height: auto;
      padding: 10rem 10% 0;
      overflow-y: visible;
      justify-content: flex-start;
    }
  }
}
</style>
